import * as React from 'react';
import * as Autosuggest from 'react-autosuggest';
import { overrideCSS, getTypeaheadResults, isUndefined, isNullOrUndefined, getUrlParameter, isShareDisabled, addItemToGoogleTracking } from '../../utils';
import { IExplorerProperties, ITypeahead, ISearch } from "../../models";
import { TYPEAHEAD_SEARCH_VALUES } from "../../config";
import { paramCase, titleCase } from "change-case";
import { Tooltip } from "../../assets/Tooltip/Tooltip";

// tslint:disable-next-line:variable-name
let FaSearch = require('react-icons/lib/fa/search');
// tslint:disable-next-line:variable-name
let FaAdd = require('react-icons/lib/fa/plus');
const styles = require('./Search.pcss');
const internalStyles = require('./Search.pcss');

/*-- Interfaces --*/

interface IProps {
    styles?: Object;
    typeahead?: ITypeahead[];
    isFundPage?: boolean;
    isMiniFinder?: boolean;
    isFilterSidebar?: boolean;
    isCompare?: boolean;
    compareFn?: Function;
    miniFinderSearch?: ISearch[];
    setTermFn?: Function;
}

interface IState {
    value?: string;
    suggestions?: any[];
    hasFunds?: boolean;
    searchHasRun?: boolean;
}

/*-- Component --*/

export class Search extends React.Component<IExplorerProperties & IProps, IState>{
    constructor(props) {
        super(props);

        this.state = {
            value: '',
            suggestions: [],
            hasFunds: true,
            searchHasRun: false
        };
        this.getSuggestions = this.getSuggestions.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.doSearch = this.doSearch.bind(this);
    }

    componentDidMount() {
        let { isMiniFinder, isFilterSidebar } = this.props;
        let url = location.search;
        let searchString = getUrlParameter(url, "term");
        let type = getUrlParameter(url, "type");
        let fundName = getUrlParameter(url, "fund_name");

        if (!isNullOrUndefined(fundName)) {
            this.setState({ value: titleCase(fundName) });
        }

        if (!isNullOrUndefined(searchString) && !isMiniFinder && !isFilterSidebar) {
            this.setState({
                value: searchString
            });

            this.doSearch(null, searchString, type === "group", type === "match")
        }
    }

    // Teach Autosuggest how to calculate suggestions for any given input value.
    getSuggestions(value, isReset = false) {
        const { typeahead, isFundPage, isMiniFinder, filter, miniFinderSearch } = this.props;
        let notExplorer = isFundPage || isMiniFinder;
        const inputValue = value.trim().toLowerCase();
        const inputLength = inputValue.length;
        let typeaheadResults = [];
        if (inputLength !== 0) {
            let currentSearch = [];
            if (!isReset) {
                currentSearch = miniFinderSearch || filter.search;
            }
            typeaheadResults = getTypeaheadResults(typeahead || [], inputValue, TYPEAHEAD_SEARCH_VALUES, currentSearch, notExplorer);
        }
        return typeaheadResults;
    };

    // When suggestion is clicked, Autosuggest needs to populate the input element
    // based on the clicked suggestion. Teach Autosuggest how to calculate the
    // input value for every given suggestion.
    getSuggestionValue(suggestion) {
        return `${suggestion.display.legal_name} - ${suggestion.isin}`;
    };

    getSectionSuggestions(section) {
        return section.suggestions;
    }

    renderSectionTitle(section) {
        if (isNullOrUndefined(this.props.compareFn)) {
            return (
                <p onClick={(event) => this.doSearch(event, section.title, true, undefined, true)}>
                    <strong>{section.title}</strong><br />
                    <small>View all share classes</small>
                </p>
            );
        } else {
            return null;
        }
    }

    // Use your imagination to render suggestions.
    renderSuggestion(suggestion: ITypeahead) {
        const { appParams, isFundPage, isCompare } = this.props;
        const isUK = appParams.region.toLowerCase() === "uk";
        const noFunPage = appParams.productrange.toLowerCase() === "ppl" || isShareDisabled(suggestion.isin);
        let onClickFn;
        let searchLinkDisabled;

        if (isCompare) {
            onClickFn = (event, isin) => null;
        }
        else if (noFunPage) {
            onClickFn = (event, isin) => event.stopPropagation();
            searchLinkDisabled = "searchLinkDisabled";
        }
        else {
            onClickFn = this.doSearch;
        }

        return (
            <a onClick={(event => onClickFn(event, suggestion.isin.toLowerCase()))} className={`${styles.autosuggestItem} ${searchLinkDisabled}`}>
                <p>{suggestion.display.legal_name} - {suggestion.display.shareclass_display} {suggestion.display.shareclass_currency_name}</p>
                <p className={styles.smallText}>
                    {`ISIN: ${suggestion.isin}`}
                    {suggestion.display.valoren !== "" && !isUK && !isFundPage ? ` - Valoren: ${suggestion.display.valoren}` : ""}
                    {suggestion.display.wkn !== "" && !isUK && !isFundPage ? ` - WKN: ${suggestion.display.wkn}` : ""}
                    <br />
                    {!noFunPage && <small>View fund page</small>}
                </p>
            </a>
        );
    };

    onChange(event, { newValue }) {
        let { setTermFn } = this.props;
        if (setTermFn) {
            this.props.setTermFn(newValue);
        }
        return this.setState({
            value: newValue
        });
    };

    // Autosuggest will call this function every time you need to update suggestions.
    // You already implemented this logic above, so just use it.
    onSuggestionsFetchRequested({ value }) {
        return this.setState({
            suggestions: this.getSuggestions(value)
        });
    };

    // Autosuggest will call this function every time you need to clear suggestions.
    onSuggestionsClearRequested() {
        return this.setState({
            suggestions: []
        });
    };

    handleSearch(event) {
        event.preventDefault();
        let inputValue = event.target.children[0].children[0].value;

        this.doSearch(event, inputValue, false, true);
    }

    doSearch(event, inputValue, isGroupSearch: boolean = false, isMatchSearch: boolean = false, isReset = false) {
        let { changeFilter, appParams, isMiniFinder, isFundPage } = this.props;
        let searchHasRun = true;
        if (!isUndefined(changeFilter)) {
            if (isMiniFinder) {
                addItemToGoogleTracking(appParams, "find_your_fund_search", { eventCategory: inputValue, eventLabel: 'search result selected' });
            }
            else {
                addItemToGoogleTracking(appParams, "search_filter", { eventCategory: inputValue, eventLabel: 'search result selected' });
            }

            let suggestions = [...this.getSuggestions(inputValue, isReset)];
            let isins = suggestions.map(item => item.suggestions.map(suggestion => suggestion.isin));
            let search = null;
            let url = `${appParams.baseurl}${appParams.fundExplorerUrl}/${process.env.NODE_ENV === "local" ? `${appParams.productrange}/${appParams.region}/${appParams.clienttype}/${appParams.language}` : ""}?term=${encodeURIComponent(inputValue)}`
            if (suggestions.length > 0) {
                if (isGroupSearch) {
                    if (isMiniFinder || isFundPage) {
                        window.location.href = `${url}&type=group`;
                    }
                    else {
                        search = [{
                            property: "isin",
                            matchtype: "MATCH",
                            values: isins.shift()
                        }];
                    }
                }
                else if (isMatchSearch) {
                    if (isMiniFinder || isFundPage) {
                        window.location.href = `${url}&type=match`;
                    } else {
                        search = [{
                            property: "isin",
                            matchtype: "MATCH",
                            values: suggestions.length === 0 ? [inputValue] : isins.reduce((acc, item) => [...acc, ...item])
                        }];
                    }
                }
                else {
                    let suggestion = suggestions.shift().suggestions.shift();
                    window.location.href = `${appParams.baseurl}funds/${paramCase(suggestion.display.fund_name)}/${inputValue}`;
                }
            }
            else {
                this.setState({
                    hasFunds: false
                });
            }

            if (isMatchSearch && inputValue !== "" && suggestions.length === 0) {
                if (isMiniFinder || isFundPage) {
                    window.location.href = `${url}&type=match`;
                } else {
                    search = [{
                        property: "isin",
                        matchtype: "MATCH",
                        values: [inputValue.split(" - ").pop()]
                    }];
                }
            }

            if (suggestions.length === 0 && inputValue === "") {
                search = [];
                searchHasRun = false;
            }

            if (!isNullOrUndefined(search)) {
                this.props.changeFilter({ search: search }, appParams, true, isReset);
            }
        }

        this.setState({
            suggestions: [],
            value: inputValue,
            searchHasRun: searchHasRun
        });
    }

    render() {
        let styles = this.props.styles ? overrideCSS(internalStyles, this.props.styles) : internalStyles;

        const { isFundPage, isMiniFinder, typeahead, isCompare, compareFn } = this.props;
        const { value, suggestions } = this.state;
        const inputProps = {
            placeholder: `Search by fund name or ISIN`,
            value,
            onChange: this.onChange.bind(this)
        };

        let submitHandler = !isCompare ? this.handleSearch : event => compareFn(event, typeahead);

        return (
            <div className={styles.searchBar}>
                <form ref="searchForm" onSubmit={(event => submitHandler(event))}>
                    <Autosuggest
                        multiSection={true}
                        suggestions={suggestions}
                        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested.bind(this)}
                        onSuggestionsClearRequested={this.onSuggestionsClearRequested.bind(this)}
                        renderSectionTitle={this.renderSectionTitle.bind(this)}
                        getSectionSuggestions={this.getSectionSuggestions.bind(this)}
                        getSuggestionValue={this.getSuggestionValue.bind(this)}
                        renderSuggestion={this.renderSuggestion.bind(this)}
                        inputProps={inputProps} />
                    <button type="submit">
                        {isCompare ? <FaAdd /> : <FaSearch />}
                    </button>
                </form>
                {!isFundPage && !isMiniFinder &&
                    <div className={styles.searchInfo}>
                        <p>{this.state.value !== "" && this.state.searchHasRun ? `Showing results for: "${this.state.value}"` : ""}</p>
                        <a onClick={event => this.doSearch(event, "")}>RESET SEARCH</a>
                    </div>
                }
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/client/components/Search/Search.tsx